export const IMAGE_JOB_POLL_INTERVAL_MS = 2000
export const IMAGE_JOB_SLOW_POLL_INTERVAL_MS = 5000
// Poll every 2 s for the first 2 minutes, then every 5 s.
export const IMAGE_JOB_FAST_POLL_WINDOW_MS = 2 * 60 * 1000
// 22 min: the worker gives each image provider call 5 minutes
// (WithImageTimeout(5*time.Minute) in apps/shared/pkg/workerapp/app.go) and
// requeues a timed-out job up to 3 more times (defaultMaxRetries = 3 in
// apps/studio/workers/aijobs/runner.go), so four attempts of up to 5 minutes
// plus queue pickup and retry delays.
export const IMAGE_JOB_MAX_WAIT_MS = 22 * 60 * 1000

// Starts an image job and polls it until it completes, fails or
// IMAGE_JOB_MAX_WAIT_MS has passed since it was created. A job requeued
// between worker attempts is reported as pending and still followed.
// createJob() and getJob(jobId) resolve { job }; onStatus receives
// { id, status, attempt } after creation and after every poll.
export async function runImageGenerationJob({ createJob, getJob, onStatus }) {
  const started = await createJob()
  const jobId = started?.job?.id
  if (!jobId) throw new Error('Image generation did not return a job ID')
  const createdAt = Date.now()
  onStatus?.({ id: jobId, status: started.job.status || 'pending', attempt: started.job.attempt })

  for (;;) {
    const response = await getJob(jobId)
    const job = response?.job || {}
    onStatus?.({ id: jobId, status: job.status || 'processing', attempt: job.attempt })
    if (job.status === 'failed') {
      const detail = typeof job.error?.message === 'string' ? job.error.message.trim() : ''
      throw new Error(detail || (job.error?.retryable ? 'Image generation failed. Try again.' : 'Image generation failed.'))
    }
    if (job.status === 'completed') return job
    const elapsed = Date.now() - createdAt
    if (elapsed >= IMAGE_JOB_MAX_WAIT_MS) break
    const interval = elapsed < IMAGE_JOB_FAST_POLL_WINDOW_MS ? IMAGE_JOB_POLL_INTERVAL_MS : IMAGE_JOB_SLOW_POLL_INTERVAL_MS
    await new Promise((resolve) => setTimeout(resolve, interval))
  }
  throw new Error('Image generation timed out. The job remains available to retry.')
}

// Server errors arrive either as { error: "message" } or as the AI error shape
// { error: { code, message, retryable } }.
export function imageGenerationErrorMessage(error, fallback = 'Failed to generate image') {
  const serverError = error?.response?.data?.error
  if (typeof serverError === 'string' && serverError.trim()) return serverError
  if (typeof serverError?.message === 'string' && serverError.message.trim()) return serverError.message
  if (typeof error?.message === 'string' && error.message.trim()) return error.message
  return fallback
}
